import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import MainCard from '@/components/MainCard';
import { useDashboardFilters } from '../../contexts/DashboardFilterContext';
import axiosInstance from "@/utils/axios";
import { buildFilterBody } from './utils';
import { SUPPORTED_SPORTS } from 'constants/globalConstants';

// ─── Tipi ───────────────────────────────────────────────────────────────────

interface KpiState {
  scoreCard: any[];
  avgDuration: any;
}

// ─── 1. COMPONENTE UNICO E PARAMETRIZZATO (UI Atomica) ───────────────────────

interface KpiCardProps {
  title: string;
  value: string | undefined;
  isLoading: boolean;
}
const CURRENT_SPORT = ((import.meta as any).env.VITE_APP_SPORT).toUpperCase();

function KpiCard({ title, value, isLoading }: KpiCardProps) {
  return (
    <MainCard
      content={false}
      sx={{        
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: '100px',
      }}
    >
      <Typography variant="body1" color="text.secondary" fontWeight={600}>
        {title}
      </Typography>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1 }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 1 }}>
          <Typography variant="h2" fontWeight={700} color="text.primary">
            {value || '0'}
          </Typography>
        </Box>
      )}      
    </MainCard>
  );
}

// ─── LERPER / PARSERS (Logiche di estrazione parametrizzate) ──────────────────

const kpiParsers = {
  aggregateScoreCard: (data: any[], targetType: 'KEY_MOMENT' | 'CLIP' | 'MATCH_HIGHLIGHT' | 'GOAL_PARADE'): string => {
    const list = Array.isArray(data) ? data : [];
    let sum = 0;

    list.forEach((item) => {
      const type = (item.content_type || '').toUpperCase().trim();
      const total = item.value || item.total_clips || 0;

      if (targetType === 'KEY_MOMENT' && type === 'KEY_MOMENT') sum += total;
      if (targetType === 'CLIP' && type === 'CLIP') sum += total;
      if (targetType === 'MATCH_HIGHLIGHT' && type.includes('HIGHLIGHT')) sum += total;
      if (targetType === 'GOAL_PARADE' && type.includes('GOAL_PARADE')) sum += total;
    });

    return sum.toLocaleString('it-IT');
  },

  formatDuration: (data: any): string => {    
    if (!data || !Array.isArray(data) || data.length === 0) return '0m 0s';
    
    const rawValue = data[0]?.value;
    if (rawValue === undefined || rawValue === null || isNaN(Number(rawValue))) {
      return '0m 0s';
    }

    const totalSeconds = Math.floor(Number(rawValue));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds}s`;
  }
};


// ─── COMPONENTE ORCHESTRATORE PRINCIPALE ──────────────────────────────────────

export default function KpiCards() {
  const { contentType, competition, season, dateRange, updateDashboard, filterLoaded } = useDashboardFilters();

  const [data, setData] = useState<KpiState>({ scoreCard: [], avgDuration: null });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if(filterLoaded){
      let cancelled = false;

      const fetchKpiData = async () => {
        try {
          setIsLoading(true);
          setError(null);

          const body = buildFilterBody(contentType || [], competition || [], season || [], dateRange);

          // Chiamate POST parallele
          const [scoreCardRes, avgHighlightRes] = await Promise.all([
            axiosInstance.post('/dashboard/getContentTypeScoreCard', body),
            axiosInstance.post('/dashboard/getAvgHighlight', body),
          ]);

          if (!cancelled) {
            setData({
              scoreCard: scoreCardRes.data,
              avgDuration: avgHighlightRes.data,
            });
          }
        } catch (err) {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : 'Failed to load analytics data');
          }
        } finally {
          if (!cancelled) {
            setIsLoading(false);
          }
        }
      };

      fetchKpiData();
      return () => { cancelled = true; };
    }
    // eslint-disable-line react-hooks/exhaustive-deps
  }, [updateDashboard, filterLoaded]); 

  // ─── 2. PARAMETRIZZAZIONE DEI KPI ───
  const kpiRegistry = [
    {
      title: 'Key Moments Generated',
      value: kpiParsers.aggregateScoreCard(data.scoreCard, 'KEY_MOMENT'),
    },
    {
      title: 'Clips Produced',
      value: kpiParsers.aggregateScoreCard(data.scoreCard, 'CLIP'),
    },
    {
      title: 'Highlights Produced',
      value: kpiParsers.aggregateScoreCard(data.scoreCard, 'MATCH_HIGHLIGHT'),
    },
    ...(
      CURRENT_SPORT === SUPPORTED_SPORTS.FOOTBALL
        ? [
            {
              title: 'Goal Parade Produced',
              value: kpiParsers.aggregateScoreCard(data.scoreCard, 'GOAL_PARADE'),
            },
          ]
        : []
    ),
    {
      title: 'Avg. Highlight Duration',
      value: kpiParsers.formatDuration(data.avgDuration),
    },
  ];

  if (error) {
    return (
      <Grid size={12}>
        <MainCard sx={{ p: 2, textAlign: 'center', color: 'error.main' }}>
          <Typography variant="body1" fontWeight={600}>{error}</Typography>
        </MainCard>
      </Grid>
    );
  }

  return (
    <>
      {kpiRegistry.map((kpi, index) => (
        <Grid key={index} size={{ xs: 12, sm: 6, md: 4, lg: 12/kpiRegistry.length }}>
          <KpiCard
            title={kpi.title}
            value={kpi.value}
            isLoading={isLoading}
          />
        </Grid>
      ))}
    </>
  );
}